import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { properties } from '../data/mock';
import { 
  MapPin, Bed, Bath, Square, ArrowLeft, Phone, Mail, 
  Calendar, Star, Share2, Heart, ChevronLeft, ChevronRight 
} from 'lucide-react';

const PropertyDetail = () => {
  const { id } = useParams();
  const property = properties.find(p => p.id === parseInt(id));
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isWishlisted, setIsWishlisted] = useState(false);

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Property not found</h2>
          <Button asChild>
            <Link to="/properties">Back to Properties</Link>
          </Button>
        </div>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % property.gallery.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + property.gallery.length) % property.gallery.length);
  };

  const relatedProperties = properties.filter(p => 
    p.id !== property.id && p.location.includes(property.location.split(',')[0])
  ).slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/properties" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Properties
            </Link>
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Image Gallery */}
            <div className="relative mb-8">
              <div className="aspect-w-16 aspect-h-10 rounded-lg overflow-hidden">
                <img
                  src={property.gallery[currentImageIndex]}
                  alt={property.title}
                  className="w-full h-96 object-cover"
                />
              </div>
              
              {property.gallery.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 transition-colors"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 transition-colors"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </>
              )}
              
              <div className="absolute bottom-4 left-4 flex gap-2">
                <Badge className="bg-[#E2725B] text-white">
                  {property.status}
                </Badge>
                <Badge variant="secondary">
                  {property.type}
                </Badge>
              </div>
              
              <div className="absolute bottom-4 right-4 text-white bg-black/50 px-3 py-1 rounded">
                {currentImageIndex + 1} / {property.gallery.length}
              </div>
            </div>

            {/* Property Details */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h1 className="text-3xl font-bold mb-2" style={{ color: '#2E2E2E' }}>
                    {property.title}
                  </h1>
                  <div className="flex items-center text-gray-600 mb-4">
                    <MapPin className="h-5 w-5 mr-2" />
                    <span className="text-lg">{property.location}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsWishlisted(!isWishlisted)}
                    className={isWishlisted ? 'text-red-500 border-red-500' : ''}
                  >
                    <Heart className={`h-4 w-4 ${isWishlisted ? 'fill-current' : ''}`} />
                  </Button>
                  <Button variant="outline" size="sm">
                    <Share2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center">
                  <Bed className="h-5 w-5 mr-2 text-gray-600" />
                  <span className="font-medium">{property.bedrooms} Bedrooms</span>
                </div>
                <div className="flex items-center">
                  <Bath className="h-5 w-5 mr-2 text-gray-600" />
                  <span className="font-medium">{property.bathrooms} Bathrooms</span>
                </div>
                <div className="flex items-center">
                  <Square className="h-5 w-5 mr-2 text-gray-600" />
                  <span className="font-medium">{property.sqft} sq ft</span>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-semibold mb-3" style={{ color: '#2E2E2E' }}>Description</h3>
                <p className="text-gray-700 leading-relaxed">{property.description}</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: '#2E2E2E' }}>Key Features</h3>
                <div className="grid grid-cols-2 gap-2">
                  {property.features.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <Star className="h-4 w-4 mr-2 text-[#FF914D]" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Price & Contact */}
            <Card className="mb-6">
              <CardHeader>
                <div className="text-center">
                  <div className="text-4xl font-bold mb-2" style={{ color: '#E2725B' }}>
                    £{property.price.toLocaleString()}
                  </div>
                  <p className="text-gray-600">Asking Price</p>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button 
                  className="w-full bg-gradient-to-r from-[#E2725B] to-[#FF914D] hover:from-[#d65a43] hover:to-[#e8823d] text-white py-3 font-semibold"
                  asChild
                >
                  <Link to="/contact">Contact Agent</Link>
                </Button>
                <Button variant="outline" className="w-full flex items-center justify-center gap-2">
                  <Phone className="h-4 w-4" />
                  020 7123 4567
                </Button>
                <Button variant="outline" className="w-full flex items-center justify-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Book Viewing
                </Button>
                <Button variant="outline" className="w-full flex items-center justify-center gap-2">
                  <Mail className="h-4 w-4" />
                  Request Info
                </Button>
              </CardContent>
            </Card>

            {/* Property Calculator */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Mortgage Calculator</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deposit</label>
                    <div className="text-lg font-semibold" style={{ color: '#E2725B' }}>
                      £{(property.price * 0.2).toLocaleString()}
                    </div>
                    <p className="text-sm text-gray-600">20% deposit</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Payment*</label>
                    <div className="text-lg font-semibold" style={{ color: '#E2725B' }}>
                      £{Math.round((property.price * 0.8 * 0.04) / 12).toLocaleString()}
                    </div>
                    <p className="text-sm text-gray-600">*Estimated at 4% APR over 25 years</p>
                  </div>
                  <Button variant="outline" className="w-full">
                    Get Pre-approved
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Related Properties */}
        {relatedProperties.length > 0 && (
          <section className="mt-16">
            <h2 className="text-3xl font-bold mb-8" style={{ color: '#2E2E2E' }}>
              Similar Properties
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedProperties.map((relatedProperty) => (
                <Card key={relatedProperty.id} className="hover:shadow-lg transition-shadow duration-300">
                  <div className="relative">
                    <img 
                      src={relatedProperty.image} 
                      alt={relatedProperty.title}
                      className="w-full h-48 object-cover"
                    />
                    <Badge className="absolute top-4 left-4 bg-[#E2725B] text-white">
                      {relatedProperty.status}
                    </Badge>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold mb-2" style={{ color: '#2E2E2E' }}>
                      {relatedProperty.title}
                    </h3>
                    <div className="flex items-center text-gray-600 mb-2">
                      <MapPin className="h-4 w-4 mr-1" />
                      <span className="text-sm">{relatedProperty.location}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold" style={{ color: '#E2725B' }}>
                        £{relatedProperty.price.toLocaleString()}
                      </span>
                      <Button size="sm" asChild>
                        <Link to={`/property/${relatedProperty.id}`}>View</Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default PropertyDetail;